import {FormSubmitHandler} from 'platform/components';

import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {
  useGetTireConditionQuery,
  usePatchTireConditionMutation,
  usePostTireConditionMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {TireDictionaryForm} from '../../components/TireDictionaryForm/TireDictionaryForm';
import {TireDictionaryFormType} from '../../types/TireDictionaryFormType';

export function TireConditionDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const {data, isLoading, isError} = useGetTireConditionQuery(
    {tireConditionId: id ?? ''},
    {skip: isNil(id)}
  );
  const [postTireCondition] = usePostTireConditionMutation();
  const [patchTireCondition] = usePatchTireConditionMutation();

  const handleSubmit: FormSubmitHandler<TireDictionaryFormType> = async (
    formData,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const body = {
      name: formData.name,
    };

    if (isNil(id)) {
      return await postTireCondition({
        body,
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          navigate(settingsRoutes.tireCondition);
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await patchTireCondition({
      tireConditionId: id,
      body,
    })
      .unwrap()
      .then(() => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        navigate(settingsRoutes.tireCondition);
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  return (
    <SettingsTemplate
      header={{
        title: data?.name ?? i18n.t('entity.tiresInventory.labels.newTireCondition'),
        breadcrumbs: [
          {
            label: i18n.t('module.workshop.title'),
            isCurrentPage: true,
          },
          {
            label: i18n.t('entity.tiresInventory.labels.tireCondition'),
            href: settingsRoutes.tireCondition,
          },
        ],
      }}
      isLoading={isLoading}
      isError={isError}
      isCreating={isNil(id)}
      data-testid={testIds.settings.tireConditionDetail('template')}
    >
      <TireDictionaryForm
        onSubmit={handleSubmit}
        defaultValues={{name: data?.name}}
        onDiscard={(navigateWithoutBlocker) => navigateWithoutBlocker(settingsRoutes.tireCondition)}
        data-testid={testIds.settings.tireConditionDetail('form')}
      />
    </SettingsTemplate>
  );
}
